import React, { useEffect, useMemo, useState, useCallback } from "react"
import {
  Button,
  Badge,
  UncontrolledTooltip,
  Row,
  Col,
  Progress,
  ButtonGroup,
} from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import VehicleContentsModal from "./VehicleContentsModal"
import { useVehicleStockPublishes } from "hooks/useVehicleStockPublishes"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import {
  calculatePublishPercentage,
  getCurrentContentCount,
} from "helpers/vehicle_stock_publish_helpers"

const VehicleStockPublishList = ({
  params,
  setPage,
  setSearch,
  updateParams,
  countsData,
}) => {
  const { platforms } = useFilteredVehicleOptions()
  const [modal, setModal] = useState(false)
  const [selectedData, setSelectedData] = useState(null)

  const handleOpenModal = useCallback(
    (rowData, resetPage = true) => {
      setSelectedData(rowData)
      updateParams(prev => ({
        ...prev,
        p_modal: true,
        stock_id: rowData?.id,
        ...(resetPage && { p_page: 1 }),
      }))
      setModal(true)
    },
    [updateParams]
  )

  const handleCloseModal = useCallback(() => {
    setModal(false)
    setSelectedData(null)
    updateParams(prev => {
      const newParams = { ...prev }
      delete newParams.p_modal
      delete newParams.stock_id
      delete newParams.p_page
      return newParams
    })
  }, [updateParams])

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.slug && { slug: params.slug }),
      ...(params.search && { search: params.search }),
      pending: params.pending,
    }),
    [params.page, params.search, params.slug, params.pending]
  )

  const {
    data: publishResponse,
    isLoading,
    refetch: publishRefetch,
    isRefetching: publishRefetchLoading,
  } = useVehicleStockPublishes(queryParams)

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      {
        header: "Vehicle",
        accessorKey: "stock.stock_id",
        cell: ({ row }) => row.original?.stock?.stock_id ?? "-",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const contents = row.original?.contents ?? []
          const summary = calculatePublishPercentage(contents)
          return (
            <>
              <Button
                color="primary"
                size="sm"
                onClick={() => handleOpenModal(row.original)}
              >
                View Contents
              </Button>
              <div className="mt-2">
                <Progress
                  value={summary.percentage}
                  color="info"
                  className="bg-light"
                  animated
                />
                <small>
                  {summary.published}/{summary.total}
                </small>
              </div>
            </>
          )
        },
      },
    ],
    [handleOpenModal]
  )

  const publishData = publishResponse?.data?.data?.data || []
  const paginationInfo = publishResponse?.data?.data

  const currentPlatform =
    platforms?.find(item => item?.slug === params?.slug) || {}

  const currentPlatformCount = getCurrentContentCount(countsData, params?.slug)

  // Reopen modal if URL params say so
  useEffect(() => {
    if (params?.p_modal === "true" && params?.stock_id && !modal) {
      const foundItem = publishData.find(
        v => Number(v.id) === Number(params.stock_id)
      )
      if (foundItem) handleOpenModal(foundItem, false)
    }
  }, [params?.p_modal, params?.stock_id, publishData, handleOpenModal, modal])

  useEffect(() => {
    if (modal && selectedData?.id) {
      const updated = publishData.find(v => v.id === selectedData.id)
      if (updated && updated !== selectedData) {
        setSelectedData(updated) // 🔥 trigger re-render with new data
      }
    }
  }, [publishData, selectedData?.id, modal])

  return (
    <>
      <div className="d-flex align-items-center mb-3">
        <h5 className="mb-0 flex-grow-1">
          Vehicle Content List On{" "}
          <Badge color="info">{currentPlatform?.name ?? "-"}</Badge>
        </h5>
        <div className="flex-shrink-0">
          <Button
            color="light"
            onClick={publishRefetch}
            className="ms-2"
            id="refreshTooltip"
          >
            <i
              className={`mdi mdi-refresh ${
                publishRefetchLoading ? "mdi-spin" : ""
              }`}
            />
            <UncontrolledTooltip
              placement="top"
              target="refreshTooltip"
              timeout={0}
            >
              Refresh
            </UncontrolledTooltip>
          </Button>
        </div>
      </div>

      <Row className="mb-3">
        <Col sm="4" className="mb-3 mb-sm-0">
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>

        <Col sm="8" className="d-flex justify-content-end">
          <ButtonGroup>
            <Button
              color={params.p_all === "all" ? "primary" : "light"}
              onClick={() =>
                updateParams(prev => ({
                  ...prev,
                  pending: "",
                  p_all: "all",
                  page: 1,
                }))
              }
            >
              All{" "}
              <Badge color="info">
                {(currentPlatformCount?.total_pending ?? 0) +
                  (currentPlatformCount?.total_posted ?? 0)}
              </Badge>
            </Button>
            <Button
              color={
                params.pending === true || params.pending === "true"
                  ? "primary"
                  : "light"
              }
              onClick={() =>
                updateParams(prev => ({
                  ...prev,
                  pending: true,
                  p_all: "false",
                  page: 1,
                }))
              }
            >
              Pending{" "}
              <Badge color="info">
                {currentPlatformCount?.total_pending ?? 0}
              </Badge>
            </Button>
            <Button
              color={params.pending === "false" ? "primary" : "light"}
              onClick={() =>
                updateParams(prev => ({
                  ...prev,
                  pending: false,
                  p_all: "false",
                  page: 1,
                }))
              }
            >
              Posted{" "}
              <Badge color="info">
                {currentPlatformCount?.total_posted ?? 0}
              </Badge>
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={publishData}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || publishRefetchLoading}
      />

      {/* ✅ Global modal (not per row) */}
      <VehicleContentsModal
        modal={modal}
        toggle={handleCloseModal}
        selectedData={selectedData}
        params={params}
        updateParams={updateParams}
      />
    </>
  )
}

VehicleStockPublishList.displayName = "VehicleStockPublishList"
export default React.memo(VehicleStockPublishList)
